import { useEffect, useRef, useState } from "react";

// Polls the report endpoint until the AI narrative is ready, then renders it.
// The deterministic analytics are server-rendered around this island; this only
// owns the AI section. A transient network blip or one-off non-ok response does
// NOT end the loop — we retry (up to MAX_ERRORS consecutive failures) so a
// momentary hiccup can't permanently hide a report that's on its way. When the
// poll bound is reached while still generating, or generation truly fails, we
// show a short status line rather than silently vanishing.
const MAX_ERRORS = 5;

export default function MockPerformanceReport({ endpoint, poll_interval = 3000, max_polls = 40 }) {
  const [state, setState] = useState({ status: "loading" });
  const timer = useRef(null);
  const polls = useRef(0);
  const errors = useRef(0);

  useEffect(() => {
    let active = true;
    polls.current = 0;
    errors.current = 0;

    async function load() {
      try {
        const res = await fetch(endpoint, { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!active) return;
        errors.current = 0;
        setState(data);
        if (data.status === "pending") schedulePoll();
      } catch {
        if (!active) return;
        errors.current += 1;
        if (errors.current >= MAX_ERRORS) {
          setState({ status: "error" });
          return;
        }
        // Transient — keep the section in its "building" state and retry.
        setState((prev) => (prev.status === "ready" ? prev : { status: "pending" }));
        timer.current = setTimeout(load, poll_interval);
      }
    }

    function schedulePoll() {
      polls.current += 1;
      if (polls.current >= max_polls) {
        setState({ status: "slow" });
        return;
      }
      timer.current = setTimeout(load, poll_interval);
    }

    load();
    return () => {
      active = false;
      clearTimeout(timer.current);
    };
  }, [endpoint, poll_interval, max_polls]);

  // `absent` (no report was ever started) and `loading` (initial flash) render
  // nothing; every other state shows the card so the section never disappears.
  if (state.status === "absent" || state.status === "loading") {
    return null;
  }

  const n = state.narrative || {};

  return (
    <section style={styles.card}>
      <div style={styles.eyebrow}>
        <i className="fa-solid fa-sparkles" style={{ marginRight: 8 }} />
        Your coach's read
      </div>

      {state.status === "ready" ? (
        <>
          {n.headline_diagnosis && <p style={styles.headline}>{n.headline_diagnosis}</p>}
          {n.what_this_means && <p style={styles.body}>{n.what_this_means}</p>}

          {Array.isArray(n.prioritized_actions) && n.prioritized_actions.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionLabel}>What to do next</div>
              <ol style={styles.actions}>
                {n.prioritized_actions.map((a, i) => (
                  <li key={i} style={styles.action}>
                    <div style={styles.actionFocus}>{a.focus_area}</div>
                    {a.why && <div style={styles.actionWhy}>{a.why}</div>}
                    {Array.isArray(a.concrete_steps) && a.concrete_steps.length > 0 && (
                      <ul style={styles.steps}>
                        {a.concrete_steps.map((s, j) => <li key={j}>{s}</li>)}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div style={styles.grid}>
            <Block label="Pacing" body={n.pacing_insight} />
            <Block label="Readiness" body={n.readiness_verdict} accent="var(--accent, #0b6b7a)" />
          </div>

          {Array.isArray(n.study_sequence) && n.study_sequence.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionLabel}>Your plan for the coming days</div>
              <ol style={styles.plan}>
                {n.study_sequence.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}

          <p style={styles.disclaimer}>
            Generated from this mock. A study aid, not medical advice.
          </p>
        </>
      ) : (
        <StatusLine status={state.status} />
      )}
    </section>
  );
}

// Non-ready states. `pending` spins; `slow` (poll bound hit while still
// generating) and `failed`/`error` explain what happened and how to recover,
// so the section is never silently empty.
function StatusLine({ status }) {
  if (status === "slow") {
    return (
      <p style={styles.pending}>
        This is taking longer than usual. We'll email you when it's ready — or
        refresh this page in a moment.
      </p>
    );
  }

  if (status === "failed" || status === "error") {
    return (
      <p style={styles.pending}>
        We couldn't put your report together just now. Refresh this page to try
        again.
      </p>
    );
  }

  return (
    <p style={styles.pending}>
      <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8 }} />
      Building your report…
    </p>
  );
}

function Block({ label, body, accent }) {
  if (!body) return null;
  return (
    <div>
      <div style={{ ...styles.sectionLabel, color: accent || "var(--ink-900, #0b1420)" }}>{label}</div>
      <p style={styles.body}>{body}</p>
    </div>
  );
}

const styles = {
  card: {
    border: "1px solid var(--ink-200, #e3e6ec)",
    borderRadius: "12px",
    background: "var(--paper, #fff)",
    padding: "20px 22px",
    margin: "0 0 24px",
    fontFamily: "var(--ui, system-ui)",
  },
  eyebrow: {
    fontSize: "12px", fontWeight: 700, letterSpacing: "0.05em",
    textTransform: "uppercase", color: "var(--accent-ink, #0b6b7a)", marginBottom: "14px",
  },
  headline: { margin: "0 0 10px", fontSize: "17px", fontWeight: 700, lineHeight: 1.4, color: "var(--ink-900, #0b1420)" },
  body: { margin: "0 0 14px", fontSize: "14px", lineHeight: 1.6, color: "var(--ink-700, #2b3440)" },
  section: { margin: "0 0 18px" },
  sectionLabel: { fontSize: "12px", fontWeight: 700, marginBottom: "8px", color: "var(--ink-900, #0b1420)" },
  actions: { margin: 0, paddingLeft: "18px" },
  action: { marginBottom: "12px" },
  actionFocus: { fontSize: "14px", fontWeight: 600, color: "var(--ink-900, #0b1420)" },
  actionWhy: { fontSize: "13px", color: "var(--ink-600, #4b5563)", margin: "2px 0 4px" },
  steps: { margin: "4px 0 0", paddingLeft: "16px", fontSize: "13px", lineHeight: 1.55, color: "var(--ink-700, #2b3440)" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "18px" },
  plan: { margin: 0, paddingLeft: "18px", fontSize: "14px", lineHeight: 1.7, color: "var(--ink-700, #2b3440)" },
  pending: { margin: 0, fontSize: "14px", color: "var(--ink-500, #6b7480)" },
  disclaimer: { margin: "6px 0 0", fontSize: "11px", color: "var(--ink-400, #9aa3ad)" },
};
